import React, { createContext, useContext, useState, useEffect } from "react";
import { User, onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/firebase";
import { db } from "../services/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  userUid: string | null;
  userSetupCompleted: boolean | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userUid: null,
  userSetupCompleted: null,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userUid, setUserUid] = useState<string | null>(null);
  const [userSetupCompleted, setUserSetupCompleted] = useState<boolean | null>(null);

  useEffect(() => {
    if (!auth) {
      console.error("Firebase auth is not initialized.");
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          // `authUid`から`userUid`を取得
          const usersRef = collection(db, "users");
          const q = query(usersRef, where("authUid", "==", currentUser.uid));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            setUserUid(userDoc.id); // userUid（ドキュメントID）
            setUserSetupCompleted(true); // 基本情報が登録済み
          } else {
            setUserSetupCompleted(false); // 基本情報未登録
          }
        } catch (error) {
          console.error("Error fetching userUid:", error);
          setUserSetupCompleted(false); // エラー時は未登録扱い
        }
      } else {
        setUserUid(null);
        setUserSetupCompleted(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        userUid,
        userSetupCompleted,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
